/**
 * The public marketing pages of www.perzo.art, in ONE registry: the client routes and per-page metadata
 * (pages/site), the server's page renderer and route classification (http/site.ts), robots.txt, sitemap.xml and
 * llms.txt all read it. Commercial facts (prices, trial, install availability) are never written here: they come
 * from the public plans DTO at render time.
 */

import { SITE_ORIGIN } from './siteOrigin.js';
import { HELP_ARTICLES, HELP_INDEX_PATH, helpPath } from './helpArticles.js';

export { SITE_ORIGIN };

/** What a page is for (the operator admin groups the site by it; nothing public reads it). */
export type SiteRouteKind = 'core' | 'seo' | 'seasonal' | 'help' | 'ad' | 'legal' | 'utility';

export interface SiteRoute {
  path: string;
  kind: SiteRouteKind;
  /** Short name (llms.txt link text). */
  label: string;
  title: string;
  description: string;
  /** /get-started while installs are open (the page is then the install page, not the early-access form). */
  descriptionInstallOpen?: string;
  /** llms.txt text when `description` depends on the launch state (llms.txt is not re-rendered when it changes). */
  llmsDescription?: string;
  /** false: the page carries `noindex` and stays out of the sitemap and llms.txt. */
  indexable: boolean;
  /** The robots value of a page that is not indexable, when it is more than `noindex` (meta tag and X-Robots-Tag). */
  robots?: 'noindex,nofollow';
  /**
   * false: the server renders the page without the commercial snapshot (plans, install state, demo switch), so it
   * never waits for it; the browser loads that data after hydration.
   */
  publicData?: boolean;
  /**
   * An article (a guide): the dates shown on the page, stated in its JSON-LD (Article) and used as its sitemap
   * lastmod. ISO dates (YYYY-MM-DD), changed by hand when the text changes.
   */
  article?: { datePublished: string; dateModified: string };
}

/**
 * Seed value for data the server could not get in time (see web lib/query.ts isSeedUnavailable): the page shows its
 * unavailable state, never invented data.
 */
export const SEED_UNAVAILABLE_FLAG = '$unavailable';
export const SEED_UNAVAILABLE: Readonly<Record<string, true>> = { [SEED_UNAVAILABLE_FLAG]: true };

export const SITE_ROUTES: readonly SiteRoute[] = [
  {
    path: '/',
    kind: 'core',
    label: 'Home',
    title: 'Perzo — sell custom art from your customers’ photos on Shopify',
    description:
      'A Shopify app that turns each customer’s photo into art in your store’s style, shows it on your product before checkout and prepares a print-ready file for each order.',
    indexable: true,
  },
  {
    path: '/models',
    kind: 'core',
    label: 'Styles',
    title: 'Styles · Perzo',
    description:
      'Start from an editable recipe or a blank brief, calibrate it with instructions, reference images and test photos, and publish when results hold up. Works on portraits, couples, pets and keepsakes.',
    indexable: true,
  },
  {
    path: '/try',
    kind: 'core',
    label: 'Live demo',
    title: 'Try it on your photo · Perzo',
    description: 'Upload a photo, frame it, pick a style and see it as artwork: the same flow shoppers get on a Perzo product page. Watermarked live demo.',
    indexable: true,
  },
  {
    path: '/get-started',
    kind: 'core',
    label: 'Get started',
    title: 'Get started · Perzo',
    description: 'Get early access to Perzo, the Shopify app that turns each customer’s photo into art in your store’s style. Perzo is launching soon.',
    descriptionInstallOpen:
      'Install Perzo on a Shopify store, set up a product, create a style from a reference image with Perzo Guide, and test it on sample photos before shoppers see it.',
    llmsDescription: 'How a Shopify store starts with Perzo: availability, setup steps and what a store needs.',
    indexable: true,
  },
  {
    path: '/photo-to-art-shopify-app',
    kind: 'seo',
    label: 'Photo to art Shopify app',
    title: 'Photo to art Shopify app for custom products · Perzo',
    description:
      'Perzo turns each shopper’s photo into art in your store’s style, previews it on your product mockup and prepares a print-ready file for the order.',
    llmsDescription:
      'What a photo-to-art Shopify app is, how Perzo works from the shopper’s photo to the print file, what merchants control and what Perzo does not do yet.',
    indexable: true,
  },
  {
    path: '/pet-portrait-app',
    kind: 'seo',
    label: 'Pet portrait app for Shopify',
    title: 'Shopify app for custom pet portraits · Perzo',
    description:
      'For Shopify stores: shoppers upload a pet photo, see a portrait in your style on your product mockup and approve it. Perzo prepares the print file.',
    llmsDescription: 'How a Shopify store sells custom pet portraits with Perzo: style, testing, mockups, shopper photo tips, approval and print files.',
    indexable: true,
  },
  {
    path: '/guides/sell-custom-pet-portraits-on-shopify',
    kind: 'seo',
    label: 'Guide: sell custom pet portraits on Shopify',
    title: 'How to sell custom pet portraits on Shopify (guide)',
    description:
      'A step-by-step guide for Shopify merchants: choose products, price them, set up a pet portrait style, test it, collect good photos and fulfill orders.',
    indexable: true,
    article: { datePublished: '2026-10-08', dateModified: '2026-10-08' },
  },
  {
    path: '/guides/best-ai-photo-to-art-apps-shopify',
    kind: 'seo',
    label: 'Guide: AI photo-to-art apps for Shopify',
    title: 'AI photo-to-art apps for Shopify: a sourced comparison',
    description:
      'A non-exhaustive, dated comparison of Shopify apps whose listings say they turn shopper photos into AI art: art, previews, print files, pricing, trials.',
    llmsDescription:
      'A non-exhaustive, alphabetical, sourced comparison of Shopify apps that turn shopper photos into AI art (Perzo included and disclosed), with how to choose and when another app fits better.',
    indexable: true,
    article: { datePublished: '2026-10-08', dateModified: '2026-10-08' },
  },
  {
    path: '/guides/sell-christmas-pet-portraits-shopify',
    kind: 'seasonal',
    label: 'Guide: sell Christmas pet portraits on Shopify',
    title: 'Sell Christmas pet portraits on Shopify (2026 guide)',
    description:
      'A seasonal guide for Shopify merchants: holiday products, a setup timeline, your own order-by date, holiday styles, photo tips, pricing and fulfillment.',
    llmsDescription:
      'A seasonal guide for Shopify merchants selling Christmas pet portraits and photo gifts: products, timeline and order cutoff, holiday styles, shopper photo tips, pricing and manual fulfillment.',
    indexable: true,
    article: { datePublished: '2026-10-09', dateModified: '2026-10-09' },
  },
  {
    path: '/alternatives/customily',
    kind: 'seo',
    label: 'Perzo as a Customily alternative',
    title: 'Customily alternative for AI photo art · Perzo',
    description:
      'For Shopify stores selling art from shopper photos: where Customily is stronger, where Perzo differs and how their pricing compares. Not affiliated.',
    llmsDescription:
      'A dated comparison of Perzo and Customily for the photo-to-art use case: where Customily is stronger, where Perzo differs, pricing models and sources. Perzo is not affiliated with Customily.',
    indexable: true,
    article: { datePublished: '2026-10-08', dateModified: '2026-10-08' },
  },
  {
    path: '/privacy',
    kind: 'legal',
    label: 'Privacy policy',
    title: 'Privacy policy · Perzo',
    description: 'What Perzo collects, why, who processes it, how long it is kept and how to ask for access or deletion.',
    indexable: true,
  },
  {
    path: '/terms',
    kind: 'legal',
    label: 'Terms of service',
    title: 'Terms of service · Perzo',
    description: 'The terms for stores that use Perzo: plans and billing, the free trial, acceptable use, AI results, content rights and liability.',
    indexable: true,
  },
  // The help center: the index and one page per article (shared/helpArticles.ts).
  ...helpRoutes(),
  // Ad landing pages (paid traffic): no menu, one call to action. noindex, so they never compete with the content
  // pages above (same facts); out of the sitemap and llms.txt.
  {
    path: '/lp/photo-to-art-app',
    kind: 'ad',
    label: 'Photo to art app (ad landing page)',
    title: 'Photo to art app for Shopify · Perzo',
    description: 'Shoppers upload a photo, pick your style and see the art on your product before they buy. You get the print file.',
    indexable: false,
    robots: 'noindex,nofollow',
  },
  {
    path: '/lp/pet-portrait-app',
    kind: 'ad',
    label: 'Pet portrait app (ad landing page)',
    title: 'Sell AI pet portraits on Shopify · Perzo',
    description: 'Shoppers upload a pet photo, see the portrait in your style on your product and approve it. You get the print file.',
    indexable: false,
    robots: 'noindex,nofollow',
  },
  {
    path: '/lp/alternative',
    kind: 'ad',
    label: 'Photo-to-art alternative (ad landing page)',
    title: 'Photo-to-art, not a full product editor · Perzo',
    description: 'Perzo does one job on Shopify: it turns the shopper’s photo into art in your style, with approval and a print file.',
    indexable: false,
    robots: 'noindex,nofollow',
  },
  {
    path: '/lp/christmas-photo-gifts',
    kind: 'ad',
    label: 'Christmas photo gifts (ad landing page)',
    title: 'Christmas pet portraits for Shopify · Perzo',
    description: 'Holiday pet portraits from your shoppers’ photos, in your style, on your products, with a print file per order.',
    indexable: false,
    robots: 'noindex,nofollow',
  },
  {
    // Live operational data: useful to visitors, not a search result.
    path: '/status',
    kind: 'utility',
    label: 'Status',
    title: 'Status · Perzo',
    description: 'Current status of the Perzo app, art generation, the store personalizer and billing.',
    indexable: false,
    publicData: false,
  },
];

/** Help center routes, built from the typed articles (titles ≤ 60 characters, dates from `updated`). */
function helpRoutes(): SiteRoute[] {
  const index: SiteRoute = {
    path: HELP_INDEX_PATH,
    kind: 'help',
    label: 'Help center',
    title: 'Help center · Perzo',
    description: 'Short how-to guides for Shopify stores using Perzo: install, styles, products and mockups, orders, billing and recipes.',
    indexable: true,
    publicData: false,
  };
  return [
    index,
    ...HELP_ARTICLES.map((a): SiteRoute => {
      const branded = `${a.title} · Perzo Help`;
      return {
        path: helpPath(a.slug),
        kind: 'help',
        label: `Help: ${a.title}`,
        title: branded.length <= 60 ? branded : a.title,
        description: a.summary,
        indexable: true,
        publicData: false,
        article: { datePublished: '2026-10-17', dateModified: a.updated },
      };
    }),
  ];
}

export const SITE_PATHS: readonly string[] = SITE_ROUTES.map((route) => route.path);

/** Product description shared by llms.txt and the JSON-LD (the home page's meta description). */
export const SITE_SUMMARY = SITE_ROUTES[0]!.description;

/** The registry entry for an exact path (no trailing slash, no query), or null. */
export function siteRoute(path: string): SiteRoute | null {
  return SITE_ROUTES.find((route) => route.path === path) ?? null;
}

/** The robots value of a page that is not indexable: `noindex`, or the route's own (ad landing pages: also nofollow). */
export function noindexValue(route: SiteRoute | null): string {
  return route?.robots ?? 'noindex';
}

export function siteUrl(path: string): string {
  return `${SITE_ORIGIN}${path}`;
}

export interface SiteMeta {
  title: string;
  description: string;
  path: string;
}

/** Title, description and canonical path of a public page; `installOpen` only changes /get-started. */
export function siteMeta(path: string, opts: { installOpen?: boolean } = {}): SiteMeta {
  const route = siteRoute(path);
  if (!route) throw new Error(`Not a public site page: ${path}`);
  const description = opts.installOpen && route.descriptionInstallOpen ? route.descriptionInstallOpen : route.description;
  return { title: route.title, description, path: route.path };
}

/**
 * Pages of the web app that are not the marketing site (sign-in, the merchant dashboard, admin, the wizard): the
 * server answers them with the SPA shell and `noindex`. Prefix entries match on a path-segment boundary.
 */
export const APP_PAGES: readonly string[] = ['/login', '/accept-invite'];
// /my-art (the buyer's private art page) and /share (consented share links): app shells, never indexed.
export const APP_PAGE_PREFIXES: readonly string[] = ['/app', '/admin', '/personalize', '/my-art', '/share'];

function underPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

export function isAppPage(path: string): boolean {
  return APP_PAGES.includes(path) || APP_PAGE_PREFIXES.some((prefix) => underPrefix(path, prefix));
}
